"use client";

import { motion } from "motion/react";

import { cn } from "@/lib/utils";

interface StepperProps {
  currentStep: number;
  steps: { icon: React.ReactNode }[];
  orientation?: "vertical" | "horizontal";
}

export function Stepper({ currentStep, steps, orientation = "vertical" }: StepperProps) {
  const isHorizontal = orientation === "horizontal";

  return (
    <div
      className={cn("m-auto flex flex-row items-center justify-center gap-1 p-1 sm:gap-1 sm:p-2")}
    >
      {steps.map((step, index) => {
        const isCompleted = index < currentStep;
        const isCurrent = index === currentStep;
        const isLast = index === steps.length - 1;

        return (
          <div
            key={index}
            className={cn(isHorizontal ? "flex items-center" : "flex flex-col items-start")}
          >
            <motion.div
              animate={{ scale: isCurrent ? 1.1 : 1 }}
              aria-current={isCurrent}
              className={cn(
                "relative z-10 flex h-8 w-8 items-center justify-center rounded-full",
                isCompleted && "bg-accent-foreground text-primary",
                isCurrent && "bg-muted text-accent-foreground",
                !isCompleted && !isCurrent && "bg-foreground text-muted-foreground",
              )}
              initial={false}
              transition={{ duration: 0.2 }}
            >
              <span className="flex items-center text-base">{step.icon}</span>
            </motion.div>

            {!isLast && (
              <div
                className={cn(
                  "relative",
                  isHorizontal ? "mx-2 my-0 flex h-1 w-5" : "mx-0 my-2 flex h-5 w-1",
                )}
              >
                <motion.div
                  animate={
                    isHorizontal ? { scaleX: isCompleted ? 1 : 0 } : { scaleY: isCompleted ? 1 : 0 }
                  }
                  className={cn(
                    "bg-accent-foreground rounded-full",
                    isHorizontal ? "h-1 w-full" : "h-full w-1",
                  )}
                  initial={isHorizontal ? { scaleX: 0 } : { scaleY: 0 }}
                  style={isHorizontal ? { originX: 0 } : { originY: 0 }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
